import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedTime } from 'react-intl';
import styled from 'styled-components';

import {
  DecisivePlayerPickerTaskAppearanceStatus,
  FiatWalletAccountState,
  GameStatus,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, HeadlineM } from '@sorare/core/src/atoms/typography';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { CreateFiatWalletWithInterstitialModal } from '@sorare/core/src/components/fiatWallet/CreateFiatWalletWithInterstitialModal';
import { InterstitialContextModalMode } from '@sorare/core/src/components/fiatWallet/InterstitialContextModal';
import {
  DialogAdditionalParamKey,
  DialogKey,
} from '@sorare/core/src/components/navigation/WithDialogs';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { glossary } from '@sorare/core/src/lib/glossary';

import { DecisivePlayerPickerTaskClaimStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskClaimStory/index.tsx';
import { useFiatWalletValidation } from '#src/components/missions/UnclaimedTask/ClaimButton/useFiatWalletValidation.tsx';
import { DecisivePickerSlot } from '#src/components/missions/picker/DecisivePickerSlot/index.tsx';
import { DecisivePlayerPickerDialog } from '#src/components/missions/picker/DecisivePlayerPickerDialog/index.tsx';
import { hasEditableTaskAppearance } from '#src/components/missions/utils.ts';

import type { DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask } from './__generated__/index.graphql.ts';

const StyledButtonBase = styled(ButtonBase)`
  transition: scale 0.1s ease-in-out;
  &:not(:disabled):hover {
    scale: 1.1;
  }
`;

const StyledDialog = styled(Dialog)`
  padding: var(--double-unit);
`;

const DialogHeader = styled(Horizontal)`
  justify-content: flex-end;
`;

const Slots = styled(Horizontal)`
  margin-bottom: var(--triple-unit);
  --card-avatar-size: calc(var(--unit) * 6);
`;

export const DecisivePlayerPickerTaskStatus = ({
  task,
}: {
  task: DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask;
}) => {
  const { kycUnderReview, needsKYCValidation } = useFiatWalletValidation({
    task,
  });

  const [showCreateFiatWallet, setShowCreateFiatWallet] = useState(false);

  const taskIsCompleted =
    task.aasmState === ManagerProgressionTaskState.COMPLETED &&
    !hasEditableTaskAppearance(task);
  const taskIsClaimed = task.aasmState === ManagerProgressionTaskState.CLAIMED;
  const taskIsReady =
    task.aasmState === ManagerProgressionTaskState.READY ||
    hasEditableTaskAppearance(task);
  const openDialog = useOpenDialog();
  const [showDecisivePlayerPickerDialog, setShowDecisivePlayerPickerDialog] =
    useState<boolean>(false);
  const [showIncompleteTasksDialog, setShowIncompleteTasksDialog] =
    useState<boolean>(false);
  const [defaultSelectedSlotIndex, setDefaultSelectedSlotIndex] =
    useState<number>();
  const [showClaimTaskStory, setShowClaimTaskStory] = useState<boolean>(false);
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const slotNumber = task.maxAppearancesCount ?? 3;

  const normalizedTaskAppearances = Array.from(
    {
      length: slotNumber,
    },
    (_, index) => {
      return task?.taskAppearances?.find(ta => ta.index === index);
    }
  );

  const editableTaskAppearances = normalizedTaskAppearances.filter(
    t =>
      !t ||
      (t.status === DecisivePlayerPickerTaskAppearanceStatus.READY && !t.locked)
  );

  const claim = async () => {
    if (needsKYCValidation) {
      setShowCreateFiatWallet(true);
      return;
    }
    setShowClaimTaskStory(true);
    setShowIncompleteTasksDialog(false);
  };

  const onEditTaskAppearance = (index: number) => {
    setShowDecisivePlayerPickerDialog(true);
    setDefaultSelectedSlotIndex(index);
  };

  const onClaimTaskAppearance = () => {
    if (editableTaskAppearances.length > 0) {
      setShowIncompleteTasksDialog(true);
      return;
    }
    claim();
  };

  return (
    <>
      {!!showCreateFiatWallet && (
        <CreateFiatWalletWithInterstitialModal
          mode={InterstitialContextModalMode.REWARD}
          onDecline={() => setShowCreateFiatWallet(false)}
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          onDismissActivationSuccess={() => setShowCreateFiatWallet(false)}
          onClose={() => setShowCreateFiatWallet(false)}
          canDismissAfterActivation={false}
        />
      )}
      {!!taskIsClaimed && (
        <Button color="secondary" disabled size="compact">
          <FormattedMessage {...glossary.claimed} />
        </Button>
      )}
      {!!taskIsCompleted && (
        <>
          {kycUnderReview ? (
            <Button
              color="quaternary"
              size="compact"
              onClick={() => setShowCreateFiatWallet(true)}
            >
              <FormattedMessage defaultMessage="Pending" />
            </Button>
          ) : (
            <Button
              color="primary"
              size="compact"
              onClick={onClaimTaskAppearance}
            >
              <FormattedMessage {...glossary.claimNow} />
            </Button>
          )}
        </>
      )}
      {!!taskIsReady && (
        <Horizontal gap={1.5}>
          {normalizedTaskAppearances.map((taskAppearance, index) => {
            const getAction = () => {
              if (taskAppearance?.locked) {
                if (
                  taskAppearance?.status ===
                    DecisivePlayerPickerTaskAppearanceStatus.SUCCESS &&
                  task.aasmState === ManagerProgressionTaskState.COMPLETED
                ) {
                  return onClaimTaskAppearance;
                }
                if (
                  taskAppearance?.playerGameScore?.anyGame?.statusTyped ===
                    GameStatus.playing ||
                  [
                    DecisivePlayerPickerTaskAppearanceStatus.FAILURE,
                    DecisivePlayerPickerTaskAppearanceStatus.SUCCESS,
                  ].includesValue(taskAppearance?.status)
                ) {
                  return () => {
                    if (taskAppearance?.playerGameScore?.anyGame?.id) {
                      openDialog(
                        DialogKey.game,
                        taskAppearance.playerGameScore.anyGame.id,
                        {
                          additionalParams: {
                            [DialogAdditionalParamKey.playerSlugs]:
                              normalizedTaskAppearances
                                .map(ta => ta?.card?.anyPlayer.slug)
                                .filter(Boolean),
                          },
                        }
                      );
                    }
                  };
                }
                return null;
              }
              return onEditTaskAppearance;
            };

            const action = getAction();
            const hasSucceededTaskAppearanceButCannotClaim =
              taskAppearance?.status ===
                DecisivePlayerPickerTaskAppearanceStatus.SUCCESS &&
              task.aasmState !== ManagerProgressionTaskState.COMPLETED;
            return (
              <StyledButtonBase
                key={taskAppearance?.id || `slot-${index}`}
                onClick={() => {
                  action?.(index);
                }}
                disabled={!action}
              >
                <DecisivePickerSlot
                  taskAppearance={taskAppearance}
                  isSelected={false}
                  index={index}
                  showAction={!hasSucceededTaskAppearanceButCannotClaim}
                  showLockedOverlay={false}
                  mode={task.mode}
                />
              </StyledButtonBase>
            );
          })}
          {task.aasmState === ManagerProgressionTaskState.COMPLETED && (
            <>
              <BodyS>
                <FormattedMessage {...glossary.or} />
              </BodyS>
              <Button
                size="compact"
                color="tertiary"
                onClick={onClaimTaskAppearance}
              >
                <FormattedMessage {...glossary.claimNow} />
              </Button>
            </>
          )}
        </Horizontal>
      )}
      {!!showDecisivePlayerPickerDialog && (
        <DecisivePlayerPickerDialog
          taskId={task.id}
          defaultSelectedSlotIndex={defaultSelectedSlotIndex}
          onClose={() => setShowDecisivePlayerPickerDialog(false)}
          onSuccess={() => setShowDecisivePlayerPickerDialog(false)}
        />
      )}
      <StyledDialog
        open={showIncompleteTasksDialog}
        onClose={() => setShowIncompleteTasksDialog(false)}
        maxWidth="xs"
        fullScreen={false}
        hideHeader
      >
        <Vertical gap={2}>
          <DialogHeader>
            <CloseButton onClose={() => setShowIncompleteTasksDialog(false)} />
          </DialogHeader>
          <Slots gap={2} center>
            {normalizedTaskAppearances.map((taskAppearance, index) => (
              <DecisivePickerSlot
                key={taskAppearance?.id || `slot-${index}`}
                taskAppearance={taskAppearance}
                isSelected={false}
                index={index}
                showAction
                showLockedOverlay={false}
                mode={task.mode}
              />
            ))}
          </Slots>
          <Vertical>
            <HeadlineM>
              <FormattedMessage defaultMessage="Challenge incomplete" />
            </HeadlineM>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="You still have <strong>{count, plural, one {# player} other {# players}}</strong> available to edit."
                values={{
                  count: editableTaskAppearances.length,
                  strong: chunks => (
                    <BodyM as="span" color="var(--c-white)" bold>
                      {chunks}
                    </BodyM>
                  ),
                }}
              />
            </BodyM>
          </Vertical>
          <Horizontal>
            <LoadingButton
              color="quaternary"
              size="medium"
              loading={showClaimTaskStory}
              onClick={claim}
            >
              <FormattedMessage {...glossary.claimNow} />
            </LoadingButton>
            <Button
              size="medium"
              onClick={() => {
                setShowDecisivePlayerPickerDialog(true);
                setShowIncompleteTasksDialog(false);
              }}
            >
              <FormattedMessage defaultMessage="Edit Player" />
            </Button>
          </Horizontal>
          <Alert
            title={<FormattedMessage defaultMessage="Warning" />}
            message={
              <BodyS>
                <FormattedMessage
                  defaultMessage="If you claim now, you cannot claim any more rewards until tomorrow {time}."
                  values={{
                    time: (
                      <FormattedTime
                        hour="numeric"
                        value={dailyTasksExpiresAt || undefined}
                      />
                    ),
                  }}
                />
              </BodyS>
            }
          />
        </Vertical>
      </StyledDialog>
      {!!showClaimTaskStory && (
        <DecisivePlayerPickerTaskClaimStory
          task={task}
          onClose={() => {
            setShowClaimTaskStory(false);
          }}
        />
      )}
    </>
  );
};

DecisivePlayerPickerTaskStatus.fragments = {
  decisivePlayerPickerTask: gql`
    fragment DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      aasmState
      maxAppearancesCount
      mode
      taskAppearances {
        id
        status
        locked
        card {
          slug
          anyPlayer {
            slug
          }
        }
        playerGameScore {
          id
          anyGame {
            id
            statusTyped
          }
        }
        ...DecisivePickerSlot_taskAppearance
      }

      ...DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask
      ...ClaimYourRewardsStory_taskInterface
      ...useFiatWalletValidation_taskInterface
      ...hasEditableTaskAppearance_taskInterface
    }
    ${DecisivePickerSlot.fragments.taskAppearance}
    ${DecisivePlayerPickerTaskClaimStory.fragments.taskInterface}
    ${useFiatWalletValidation.fragments.taskInterface}
    ${hasEditableTaskAppearance.fragments.taskInterface}
  ` as TypedDocumentNode<DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask>,
};
